/**
 * AdminBreadcrumb -- automatic breadcrumb for ALL admin pages.
 *
 * Coverage: 100% of routes from ia-map.md section 6.2. Root crumb "Administracao"
 * always links to /admin. Human-Readable Output: no label is ever derived from the
 * URL (an unknown ?tab=, an unknown route or a nameless drill-down stops at a known
 * crumb or shows a generic i18n label).
 */

import { useLocation, useSearchParams } from 'react-router';
import { useIntl } from 'react-intl';
import { Breadcrumb, type BreadcrumbItem } from '@/components/ui/Breadcrumb';
import { TAB_HUB_PATHS, TAB_IDS, resolveTabAlias, type TabHub } from './tabIds';

type CrumbHub = Exclude<TabHub, 'accountSettings'>;
type Translate = ReturnType<typeof useIntl>['formatMessage'];
type Crumb = readonly [labelId: string, href?: string];

/** i18n keys of each tab hub and of its non-default tabs (the default tab is the hub itself). */
const HUB_CRUMBS: Record<CrumbHub, { labelId: string; tabLabelIds: Record<string, string> }> = {
  reports: {
    labelId: 'admin.nav.reports',
    tabLabelIds: {
      builder: 'admin.reports.tabs.builder',
    },
  },
  compliance: {
    labelId: 'admin.nav.compliance',
    tabLabelIds: {
      queue: 'admin.compliance.tabs.queue',
      intelligence: 'admin.compliance.tabs.intelligence',
      rules: 'admin.compliance.tabs.rules',
    },
  },
  people: {
    labelId: 'admin.nav.people',
    tabLabelIds: { teams: 'admin.people.tabs.teams', invitations: 'admin.people.tabs.invitations' },
  },
  models: {
    labelId: 'admin.nav.models',
    tabLabelIds: { access: 'admin.models.tabs.access' },
  },
  orgSettings: {
    labelId: 'admin.nav.settings',
    tabLabelIds: {
      security: 'admin.settings.tabs.security',
      customization: 'admin.settings.tabs.customization',
      ai: 'admin.settings.tabs.ia',
    },
  },
  classifier: {
    labelId: 'admin.nav.classifier',
    tabLabelIds: {
      sandbox: 'admin.classifier.tabs.sandbox',
      dashboard: 'admin.classifier.tabs.dashboard',
      taxonomy: 'admin.classifier.tabs.taxonomy',
    },
  },
  apiKeys: {
    labelId: 'admin.nav.connections.apiKeys',
    tabLabelIds: { policies: 'admin.connections.apiKeys.tabs.policies' },
  },
  // --- FEAT-035 TASK-006 connections-breadcrumb ---
  // Connections hubs follow the generic rule root > hub > tab (no "Conexoes" group crumb)
  endpoints: {
    labelId: 'admin.nav.connections.endpoints',
    tabLabelIds: {
      'how-to-connect': 'admin.connections.endpoints.tabs.howToConnect',
      test: 'admin.connections.endpoints.tabs.test',
    },
  },
  providers: {
    labelId: 'admin.nav.connections.providers',
    tabLabelIds: {
      resilience: 'admin.connections.providers.tabs.resilience',
      events: 'admin.connections.providers.tabs.events',
    },
  },
  quotaPools: {
    labelId: 'admin.nav.connections.quotaPools',
    tabLabelIds: {
      wizard: 'admin.connections.quotaPools.tabs.wizard',
      consumption: 'admin.connections.quotaPools.tabs.consumption',
    },
  },
  // --- fim FEAT-035 TASK-006 connections-breadcrumb ---
  fleet: {
    labelId: 'admin.nav.fleet',
    tabLabelIds: {
      framework: 'admin.fleet.tabs.framework',
      'access-quota': 'admin.fleet.tabs.accessQuota',
    },
  },
  // --- FEAT-036 TASK-016 (quotas-hub) ---
  quotas: {
    labelId: 'admin.nav.quotas',
    tabLabelIds: {
      budgets: 'admin.quotas.tabs.budgets',
      pools: 'admin.quotas.tabs.pools',
      effective: 'admin.quotas.tabs.effective',
    },
  },
  // --- fim FEAT-036 TASK-016 (quotas-hub) ---
  // --- FEAT-038 TASK-010 (costs-hub) ---
  costs: {
    labelId: 'admin.nav.costs',
    tabLabelIds: {
      pricing: 'admin.costs.tabs.pricing',
      budgets: 'admin.costs.tabs.budgets',
    },
  },
  // --- fim FEAT-038 TASK-010 (costs-hub) ---
  // --- FEAT-039 TASK-010 (analytics-hub) ---
  analytics: {
    labelId: 'admin.nav.analytics',
    tabLabelIds: {
      utilization: 'admin.analytics.tabs.utilization',
      cache: 'admin.analytics.tabs.cache',
      compaction: 'admin.analytics.tabs.compaction',
      providers: 'admin.analytics.tabs.providers',
    },
  },
  // --- fim FEAT-039 TASK-010 (analytics-hub) ---
  // --- FEAT-040 TASK-011 (hubs) ---
  logs: {
    labelId: 'admin.nav.logs',
    tabLabelIds: {
      upstream: 'admin.logs.tabs.upstream',
      console: 'admin.logs.tabs.console',
      timeline: 'admin.logs.tabs.timeline',
      conversations: 'admin.logs.tabs.conversations',
    },
  },
  operations: {
    labelId: 'admin.nav.operations',
    tabLabelIds: { runtime: 'admin.operations.tabs.runtime' },
  },
  // --- fim FEAT-040 TASK-011 (hubs) ---
};

/** i18n key of a hub tab, or null when the trail must stop at the hub (default or unknown tab). */
function resolveHubTab(hub: CrumbHub, raw: string): string | null {
  const id = resolveTabAlias(hub, raw);
  const ids: readonly string[] = TAB_IDS[hub];
  if (!ids.includes(id) || id === ids[0]) return null;
  return HUB_CRUMBS[hub].tabLabelIds[id] ?? null;
}

/** Routes with a fixed trail (parents link back, the last crumb is the page). */
const compliance = ['admin.nav.compliance', '/admin/compliance'] as const;
const audit = ['admin.compliance.breadcrumb.audit', '/admin/compliance/audit'] as const;
const comparisons = ['admin.nav.comparisons', '/admin/comparisons'] as const;
const usage = ['admin.nav.usageCompliance', '/admin/usage-compliance'] as const;
const STATIC_TRAILS: Record<string, readonly Crumb[]> = {
  '/admin/compliance/audit': [compliance, ['admin.compliance.breadcrumb.audit']],
  '/admin/compliance/audit/cohort': [compliance, audit, ['admin.compliance.breadcrumb.cohort']],
  '/admin/compliance/audit/messages': [compliance, audit, ['admin.compliance.breadcrumb.messages']],
  '/admin/usage-compliance': [['admin.nav.usageCompliance']],
  '/admin/comparisons/temporal': [comparisons, ['admin.comparisons.tabs.temporal']],
  '/admin/comparisons/entities': [comparisons, ['admin.comparisons.tabs.entities']],
  '/admin/comparisons': [['admin.nav.comparisons']],
  '/admin/audit-log': [['admin.nav.auditLog']],
};

function toItems(trail: readonly Crumb[], t: Translate): BreadcrumbItem[] {
  return trail.map(([labelId, href]) => ({ label: t({ id: labelId }), href }));
}

/** Hub found by exact path (plus the '/admin/connections' alias of the endpoints hub). */
function findHub(path: string): CrumbHub | null {
  if (path === '/admin/connections') return 'endpoints';
  const hubs = Object.keys(HUB_CRUMBS) as CrumbHub[];
  return hubs.find((hub) => TAB_HUB_PATHS[hub] === path) ?? null;
}

/** Build breadcrumb items from the pathname and the ?tab= param. All but the last item link. */
function buildBreadcrumb(
  pathname: string,
  tab: string | null,
  t: Translate,
  state: Record<string, unknown> | null,
): BreadcrumbItem[] {
  const root = t({ id: 'admin.breadcrumb.root' });
  const path = pathname.replace(/\/$/, '');
  if (path === '/admin') return [{ label: root }];
  const items: BreadcrumbItem[] = [{ label: root, href: '/admin' }];

  const hub = findHub(path);
  if (hub) {
    const { labelId } = HUB_CRUMBS[hub];
    const tabLabelId = tab ? resolveHubTab(hub, tab) : null;
    if (!tabLabelId) return [...items, { label: t({ id: labelId }) }];
    return [...items, { label: t({ id: labelId }), href: TAB_HUB_PATHS[hub] }, { label: t({ id: tabLabelId }) }];
  }

  const staticTrail = STATIC_TRAILS[path];
  if (staticTrail) return [...items, ...toItems(staticTrail, t)];

  // Drill-down: the entity name comes from location.state, never from the URL id
  const drill = /^\/admin\/usage-compliance\/(team|user)\/.+$/.exec(path);
  if (drill) {
    const kind = drill[1] === 'team' ? 'team' : 'user';
    const named = state?.[kind === 'team' ? 'teamName' : 'userName'] ?? state?.entityName;
    const label = typeof named === 'string' && named ? named : t({ id: `admin.breadcrumb.${kind}` });
    return [...items, ...toItems([usage], t), { label }];
  }

  // Unknown admin route: stop at the root (no label derived from the URL)
  return items;
}

export function AdminBreadcrumb({ className }: { className?: string }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const intl = useIntl();
  const state = (location.state as Record<string, unknown> | null) ?? null;
  const items = buildBreadcrumb(location.pathname, searchParams.get('tab'), intl.formatMessage, state);
  return <Breadcrumb items={items} className={className} />;
}
